/**
 * Pipe Placeholder Tool
 * 
 * Placeholder pipe for early design phase (centerline only).
 */

import { v4 as uuidv4 } from 'uuid';
import { PipePlaceholderElement, PipePlaceholderGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class PipePlaceholderTool extends BaseTool {
    id = 'pipe_placeholder';
    name = 'Pipe Placeholder';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.createPlaceholder(this.startPoint, snapped);
            this.startPoint = snapped;
            this.currentPoint = snapped;
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'pipe_placeholder',
                start: this.startPoint,
                end: this.currentPoint,
                color: '#00ff88'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createPlaceholder(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const geometryData: PipePlaceholderGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            estimatedDiameter: 100 // 100mm estimated
        };

        const placeholder: PipePlaceholderElement = {
            id: uuidv4(),
            category: 'Pipe Placeholders',
            type: 'Pipe Placeholder',
            parameters: {
                'Estimated Diameter': '100 mm',
                'System Type': 'Domestic Cold Water',
                'Length': start.distanceTo(end).toFixed(2) + ' m'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(placeholder);
    }
}
